import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, getList } from "@/lib/api";
import Modal, { inputCls, btnPrimary, btnSecondary, Field } from "@/components/Modal";
import StatusBadge from "@/components/StatusBadge";
import AllergenChips from "@/components/AllergenChips";
import Pager from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";
import { EXPERIMENT_STATUSES, LOCATION_TYPES, PAGE_SIZE, fmtDate, label } from "@/lib/constants";

export function ExperimentForm({ initial, onClose, onSaved }) {
  const { data: formulas = [] } = useQuery({
    queryKey: ["formulas-all"],
    queryFn: async () => (await api.get("/formulas", { params: { limit: 500 } })).data,
  });
  const [form, setForm] = useState(
    initial || { name: "", formula_id: "", objective: "", hypothesis: "", status: "planned", location_type: "lab", start_date: "", end_date: "" }
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, formula_id: form.formula_id || null, start_date: form.start_date || null, end_date: form.end_date || null };
      if (initial?.id) {
        await api.put(`/experiments/${initial.id}`, payload);
        toast.success("Experiment updated");
      } else {
        await api.post("/experiments", payload);
        toast.success("Experiment created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial?.id ? "Edit Experiment" : "New Experiment"} onClose={onClose} testid="experiment-form-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="experiment-form">
        <Field label="Name">
          <input data-testid="experiment-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
        </Field>
        <Field label="Linked formula">
          <select data-testid="experiment-formula-select" value={form.formula_id || ""} onChange={set("formula_id")} className={inputCls}>
            <option value="">None</option>
            {formulas.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="Objective">
          <textarea data-testid="experiment-objective-input" rows={2} value={form.objective} onChange={set("objective")} className={inputCls} />
        </Field>
        <Field label="Hypothesis">
          <textarea data-testid="experiment-hypothesis-input" rows={2} value={form.hypothesis} onChange={set("hypothesis")} className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status">
            <select data-testid="experiment-status-select" value={form.status} onChange={set("status")} className={inputCls}>
              {EXPERIMENT_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </Field>
          <Field label="Location">
            <select data-testid="experiment-location-select" value={form.location_type} onChange={set("location_type")} className={inputCls}>
              {LOCATION_TYPES.map((l) => <option key={l} value={l}>{label(l)}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Start date">
            <input data-testid="experiment-start-input" type="date" value={form.start_date || ""} onChange={set("start_date")} className={inputCls} />
          </Field>
          <Field label="End date">
            <input data-testid="experiment-end-input" type="date" value={form.end_date || ""} onChange={set("end_date")} className={inputCls} />
          </Field>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="experiment-cancel-button">Cancel</button>
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="experiment-save-button">
            {saving ? "Saving…" : "Save experiment"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function Experiments() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [status, setStatus] = useState("");
  const [location, setLocation] = useState("");
  const [offset, setOffset] = useState(0);
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["experiments", status, location, offset],
    queryFn: () => getList("/experiments", { status, location_type: location, limit: PAGE_SIZE, offset }),
  });

  const experiments = data?.items ?? [];

  return (
    <div data-testid="experiments-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-4 border-b border-[#E8DFD1]">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Experiments</h1>
          <p className="text-sm text-[#8C7A6B] mt-1">Batch trials & iterations.</p>
        </div>
        {canWrite && (
          <button onClick={() => setShowForm(true)} className={btnPrimary} data-testid="new-experiment-button">
            <Plus size={16} /> New Experiment
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="experiment-filters">
        <button
          onClick={() => { setOffset(0); setStatus(""); }}
          data-testid="status-chip-all"
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!status ? "bg-[#2A1810] text-[#FDFBF7] border-[#2A1810]" : "bg-white text-[#5C4A3E] border-[#E8DFD1] hover:border-[#C5A059]"}`}
        >
          All
        </button>
        {EXPERIMENT_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => { setOffset(0); setStatus(s === status ? "" : s); }}
            data-testid={`status-chip-${s}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${status === s ? "bg-[#2A1810] text-[#FDFBF7] border-[#2A1810]" : "bg-white text-[#5C4A3E] border-[#E8DFD1] hover:border-[#C5A059]"}`}
          >
            {label(s)}
          </button>
        ))}
        <select data-testid="location-filter" value={location} onChange={(e) => { setOffset(0); setLocation(e.target.value); }} className={`${inputCls} w-52 ml-auto`}>
          <option value="">All locations</option>
          {LOCATION_TYPES.map((l) => <option key={l} value={l}>{label(l)}</option>)}
        </select>
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-[#E8DFD1] bg-white shadow-sm" data-testid="experiments-table">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#FAF6F0] border-b border-[#E8DFD1] text-[#2A1810] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3.5 font-semibold">Name</th>
              <th className="px-4 py-3.5 font-semibold">Formula</th>
              <th className="px-4 py-3.5 font-semibold">Status</th>
              <th className="px-4 py-3.5 font-semibold">Location</th>
              <th className="px-4 py-3.5 font-semibold">Allergens</th>
              <th className="px-4 py-3.5 font-semibold">Start date</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8C7A6B]">Loading…</td></tr>}
            {!isLoading && experiments.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="experiments-empty">No experiments match these filters.</td></tr>
            )}
            {experiments.map((e) => (
              <tr
                key={e.id}
                data-testid={`experiment-row-${e.id}`}
                onClick={() => navigate(`/experiments/${e.id}`)}
                className="border-b border-[#F4EFEA] hover:bg-[#FDFBF7] cursor-pointer transition-colors"
              >
                <td className="px-4 py-3.5 font-medium text-[#2A1810]">{e.name}</td>
                <td className="px-4 py-3.5 text-[#5C4A3E]">{e.formula_name}</td>
                <td className="px-4 py-3.5"><StatusBadge value={e.status} /></td>
                <td className="px-4 py-3.5"><StatusBadge value={e.location_type} /></td>
                <td className="px-4 py-3.5"><AllergenChips allergens={e.allergens} testid={`experiment-allergens-${e.id}`} empty="none" /></td>
                <td className="px-4 py-3.5 text-[#8C7A6B]">{fmtDate(e.start_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager offset={offset} limit={PAGE_SIZE} total={data?.total ?? 0} onChange={setOffset} testid="experiments-pager" />
      </div>

      {showForm && <ExperimentForm onClose={() => setShowForm(false)} onSaved={refetch} />}
    </div>
  );
}
